import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { TIME_UNITS } from "@miplatform/constants";
import { useTheme } from "@mui/material";
import { PickersDay } from "@mui/x-date-pickers-pro";
import { DatePicker } from "./components/DatePicker";

const getDayColor = (highlightDays, day, outsideCurrentMonth) => {
  const highlightedSet = highlightDays.find(
    ({ days }) =>
      !outsideCurrentMonth && days.some((d) => d.isSame(day, TIME_UNITS.DAY))
  );

  return highlightedSet ? highlightedSet.color : "";
};

const HighlightedDay = ({
  highlightDays = [],
  day,
  outsideCurrentMonth,
  selected,
  today,
  ...other
}) => {
  const theme = useTheme();

  const { classes } = useStyles({ selected, today });

  const highlightedColor = getDayColor(highlightDays, day, outsideCurrentMonth);

  const backgroundColor = selected
    ? theme.palette.primary.main
    : highlightedColor;

  let borderRadius = "50%";
  if (highlightedColor) {
    borderRadius = selected ? "50%" : "0";
  }

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      style={{
        backgroundColor,
        borderRadius,
      }}
      className={classes.pickersDay}
    />
  );
};

/**
 * `HighlightedDatePicker` component allows users to pick a date from a calendar while highlighting
 * specific dates with custom colors.
 *
 * @component
 *
 * @param {Object} props - The props for the `HighlightedDatePicker` component.
 * @param {boolean} [props.error=false] - Whether to show an error state on the DatePicker input.
 * @param {string|null} [props.color=null] - The color of the selected date button.
 * @param {function} props.onReset - Callback function that is triggered when the reset button is pressed.
 * @param {function} props.onChange - Callback function triggered when the value of the selected date changes.
 * @param {string} props.value - The currently selected value in the date picker.
 * @param {string} props.label - Label for the input field.
 * @param {Array} [props.actions=[]] - Array of actions to render below the date picker.
 * @param {Array} [props.secondaryActions=[]] - Secondary action components to render below the date picker.
 * @param {Array} props.highlightDays - Array of objects specifying the days to be highlighted, with custom colors.
 * @param {Object} props.slots - Custom slots for customizing the date picker (e.g., `day` slot).
 * @param {Object} props.slotsProps - Custom slot props for customizing the slot elements.
 *
 * @example
 * <MiDatePicker
 *   variant={DATEPICKER_VARIANTS.HIGHLIGHT}
 *   value={moment()}
 *   onReset={() => {}}
 *   onChange={(value) => {}}
 *   color="green"
 *   actions={[DATEPICKER_ACTIONS.CLEAR, DATEPICKER_ACTIONS.CANCEL]}
 *   secondaryActions={
 *     <MiButton
 *       key="reset"
 *       title="Reset Date"
 *       onClick={() => {}}
 *       variant={BUTTON_VARIANTS.OUTLINED}
 *       color={"blue"}
 *       disabled
 *     />
 *   }
 *   highlightDays={[
 *     {
 *       days: [
 *         getDate("2024-12-10"),
 *         getDate("2024-12-11"),
 *         getDate("2024-12-12"),
 *         getDate("2024-12-13"),
 *       ],
 *       color: theme.palette.red.light,
 *     },
 *     {
 *       days: [getDate("2024-12-22"), getDate("2024-12-17")],
 *       color: theme.palette.blue.light,
 *     },
 *   ]}
 * />
 */
export const HighlightedDatePicker = ({
  slots,
  slotsProps,
  error = false,
  onReset,
  color = null,
  onChange,
  value,
  label,
  actions = [],
  secondaryActions = [],
  highlightDays,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const onToggleOpen = (value) => {
    setOpen(value);
  };

  const datePickerProp = {
    slots: {
      day: HighlightedDay,
      ...slots,
    },
    slotProps: {
      secondaryActions,
      day: {
        highlightDays,
      },
      onReset: () => {
        onReset();
        onToggleOpen(false);
      },
      onToggleOpen,
      color,
      textField: {
        size: "small",
        shrink: true,
        label,
      },
      ...slotsProps,
    },
  };

  return (
    <DatePicker
      {...rest}
      inline
      label="Date"
      format="MM/DD/YY"
      error={error}
      onOpen={() => onToggleOpen(true)}
      onClose={() => onToggleOpen(false)}
      open={open}
      value={value}
      onChange={onChange}
      actions={actions}
      {...datePickerProp}
    />
  );
};

const useStyles = makeStyles()((theme, { selected, today }) => ({
  pickersDay: {
    color: selected ? theme.palette.primary.contrastText : "inherit",
    margin: selected || today ? 1 : 0,
    paddingLeft: selected || today ? 0 : 20,
    paddingRight: selected || today ? 0 : 20,
  },
}));
